import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list-person-schedule.schema";

/**
 * Why a lead's outreach is not moving:
 *
 * - `sameCompanyStopped` — a colleague at the lead's company answered us, so this lead's outreach
 *   was ended (PREC-3855/PREC-3895). Terminal: it does not resume. Most specific of the three, so
 *   it wins whenever it applies.
 * - `outOfOffice` — the lead sent an absence auto-reply, so the next step was moved to the
 *   announced return (PREC-3894), or the outreach was ended because that return was further out
 *   than the cap (`ended`). Without this the lead reads as ordinarily scheduled while its sequence
 *   silently slipped a fortnight — the "why has nothing been sent" question support cannot answer.
 * - `paused` — every schedule of the lead has been deactivated (sender migration, campaign pause).
 */
export type LeadHoldState =
	| { kind: "sameCompanyStopped"; stoppedAt: string }
	| { kind: "outOfOffice"; until: string; ended: boolean }
	| { kind: "paused" };

/**
 * The stop cancels the row and stamps the marker in the same write, so a *canceled* stamped row is
 * what marks it. Before PREC-3895 the same stamp meant a deferral on a still-pending row; matching
 * on `canceledAt` keeps a leftover pending stamp from an in-flight deploy out of the terminal case.
 */
const isSameCompanyStopped = (schedule: ListPersonSchedule): boolean =>
	!!schedule.sameCompanyHeldAt && !!schedule.canceledAt;

/**
 * An out-of-office marker is meaningful on both a pending row (the step was moved out) and a
 * canceled one (the return was past the cap, so the outreach ended) — the two read differently to
 * the user, so `ended` carries the distinction rather than one of them being filtered away.
 */
const isOutOfOffice = (schedule: ListPersonSchedule): boolean => !!schedule.outOfOfficeUntil;

const hasScheduledStep = (schedules: ListPersonSchedule[]): boolean =>
	!!schedules.find((schedule) => schedule.processingState === "scheduled")?.scheduledFor;

export function getLeadHoldState(schedules?: ListPersonSchedule[]): LeadHoldState | null {
	if (!schedules?.length) {
		return null;
	}

	const stopped = schedules.find(isSameCompanyStopped);

	if (stopped?.sameCompanyHeldAt) {
		return { kind: "sameCompanyStopped", stoppedAt: stopped.sameCompanyHeldAt };
	}

	const absent = schedules.find(isOutOfOffice);

	if (absent?.outOfOfficeUntil) {
		return {
			kind: "outOfOffice",
			until: absent.outOfOfficeUntil,
			ended: !!absent.canceledAt,
		};
	}

	if (hasScheduledStep(schedules) && schedules.every((schedule) => schedule.active === false)) {
		return { kind: "paused" };
	}

	return null;
}
